import { useState, useEffect, useRef, useCallback } from 'react';
import * as maplibregl from 'maplibre-gl';
import { Truck, Navigation, Wifi, WifiOff } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { useQuery } from '@tanstack/react-query';
import { createApiClient, profileService, jobService } from '../services/api';
import { useFleetHub, type FleetLocationUpdate } from '../hooks/useSignalR';
import type { UserProfileDto, JobSummaryDto } from '../types';
import { MAP_STYLE_URL, createPinElement } from '../lib/mapLibre';

// ─────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────
function timeAgo(ts: string): string {
  const secs = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 60)   return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

interface CarrierMarker {
  carrierId:    string;
  carrierName:  string;
  jobId?:       string | null;
  jobReference?:string | null;
  lat:          number;
  lng:          number;
  accuracy?:    number;
  recordedAt:   string;
  stale:        boolean;
}

// ─────────────────────────────────────────────
//  MapLibre map — drivers + job routes
// ─────────────────────────────────────────────
const FLEET_ROUTES_SOURCE = 'fleet-job-routes';

function FleetMap({
  employees,
  jobs,
}: {
  employees: CarrierMarker[];
  jobs:      JobSummaryDto[];
}) {
  const containerRef  = useRef<HTMLDivElement>(null);
  const mapRef        = useRef<maplibregl.Map | null>(null);
  const driverMarkers = useRef<Map<string, maplibregl.Marker>>(new Map());
  const jobMarkers    = useRef<Map<string, { pMark: maplibregl.Marker; dMark: maplibregl.Marker }>>(new Map());
  const [mapReady, setMapReady] = useState(false);

  // Init map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: [-1.8904, 52.4862],
      zoom: 7,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }));

    map.on('load', () => {
      map.addSource(FLEET_ROUTES_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: FLEET_ROUTES_SOURCE, type: 'line', source: FLEET_ROUTES_SOURCE,
        paint: { 'line-color': '#dc2626', 'line-width': 2, 'line-opacity': 0.5, 'line-dasharray': [2.5, 1.5] },
      });
      setTimeout(() => { map.resize(); setMapReady(true); }, 150);
    });

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update driver markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const seen = new Set<string>();

    employees.forEach(d => {
      seen.add(d.carrierId);
      const onJob  = !!d.jobId;
      const color  = d.stale ? '#94a3b8' : onJob ? '#dc2626' : '#2563eb';
      const initials = d.carrierName.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);

      const jobLine = d.jobReference
        ? `<div style="font-size:12px;color:#64748b">Job: <strong>${d.jobReference}</strong></div>`
        : `<div style="font-size:12px;color:#94a3b8">Idle</div>`;
      const popupHtml = `<div style="min-width:160px">
        <div style="font-weight:600;margin-bottom:4px">${d.carrierName}</div>${jobLine}
        <div style="font-size:12px;color:#64748b;margin-top:2px">Last seen: ${timeAgo(d.recordedAt)}</div>
        ${d.accuracy ? `<div style="font-size:11px;color:#94a3b8">Accuracy: ±${Math.round(d.accuracy)}m</div>` : ''}
      </div>`;

      const existing = driverMarkers.current.get(d.carrierId);
      if (existing) {
        existing.setLngLat([d.lng, d.lat]);
        const el = existing.getElement();
        el.style.background = color;
        el.title = d.carrierName;
        existing.getPopup()?.setHTML(popupHtml);
      } else {
        const el = document.createElement('div');
        el.title = d.carrierName;
        el.style.cssText =
          `background:${color};color:white;border-radius:50%;width:34px;height:34px;` +
          'display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;' +
          'border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3);cursor:pointer;';
        el.textContent = initials;
        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([d.lng, d.lat])
          .setPopup(new maplibregl.Popup({ offset: 18 }).setHTML(popupHtml))
          .addTo(map);
        driverMarkers.current.set(d.carrierId, marker);
      }
    });

    // Remove drivers no longer present
    driverMarkers.current.forEach((m, id) => {
      if (!seen.has(id)) { m.remove(); driverMarkers.current.delete(id); }
    });

    if (employees.length > 0) {
      const coords = employees.map(d => [d.lng, d.lat] as [number, number]);
      const bounds = coords.reduce((b, c) => b.extend(c), new maplibregl.LngLatBounds(coords[0], coords[0]));
      map.fitBounds(bounds, { padding: 60, maxZoom: 13 });
    }
  }, [employees, mapReady]);

  // Draw job route lines (P→D) for jobs that have coordinates
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const mappable = jobs.filter(j => j.pickupLat && j.pickupLng && j.deliveryLat && j.deliveryLng);
    const activeJobIds = new Set(mappable.map(j => j.id));

    jobMarkers.current.forEach(({ pMark, dMark }, id) => {
      if (!activeJobIds.has(id)) {
        pMark.remove(); dMark.remove();
        jobMarkers.current.delete(id);
      }
    });

    mappable.forEach(job => {
      if (jobMarkers.current.has(job.id)) return; // already drawn

      const pMark = new maplibregl.Marker({ element: createPinElement('P', '#dc2626') })
        .setLngLat([job.pickupLng!, job.pickupLat!])
        .setPopup(new maplibregl.Popup({ offset: 12 }).setHTML(`<b>${job.referenceNumber}</b> · Pickup`))
        .addTo(map);

      const dMark = new maplibregl.Marker({ element: createPinElement('D', '#dc2626') })
        .setLngLat([job.deliveryLng!, job.deliveryLat!])
        .setPopup(new maplibregl.Popup({ offset: 12 }).setHTML(`<b>${job.referenceNumber}</b> · Delivery`))
        .addTo(map);

      jobMarkers.current.set(job.id, { pMark, dMark });
    });

    const source = map.getSource(FLEET_ROUTES_SOURCE) as maplibregl.GeoJSONSource | undefined;
    source?.setData({
      type: 'FeatureCollection',
      features: mappable.map(job => ({
        type: 'Feature', properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [[job.pickupLng!, job.pickupLat!], [job.deliveryLng!, job.deliveryLat!]],
        },
      })),
    });
  }, [jobs, mapReady]);

  return <div ref={containerRef} className="w-full h-full" />;
}

// ─────────────────────────────────────────────
//  FLEET MAP PAGE
// ─────────────────────────────────────────────
export default function FleetMapPage() {
  const { getToken, account } = useAppAuth();

  const { data: profile } = useQuery<UserProfileDto>({
    queryKey: ['profile'],
    queryFn:  () => profileService(createApiClient(getToken)).getProfile(),
    enabled:  !!account,
    staleTime: Infinity,
  });

  const carrierId = profile?.userId ?? '';

  // Carrier's active jobs — always fetch so routes appear on map
  const { data: myJobs = [] } = useQuery<JobSummaryDto[]>({
    queryKey: ['my-jobs'],
    queryFn:  () => jobService(createApiClient(getToken)).getMyJobs(),
    enabled:  !!account,
    refetchInterval: 60_000,
  });

  const [liveDrivers, setLiveDrivers] = useState<Map<string, CarrierMarker>>(new Map());
  const [connected,   setConnected]   = useState(false);
  const [,            tick]           = useState(0);

  const loadSnapshot = useCallback(async () => {
    if (!carrierId) return;
    try {
      const res = await createApiClient(getToken).get<{
        carrierId: string; carrierName: string; jobId?: string | null; jobReference?: string | null;
        latitude: number; longitude: number; accuracyMetres?: number; recordedAt: string;
      }[]>('/api/carrier/fleet/live-locations');
      setLiveDrivers(prev => {
        const next = new Map(prev);
        res.data.forEach(d => next.set(d.carrierId, {
          carrierId: d.carrierId, carrierName: d.carrierName, jobId: d.jobId,
          jobReference: d.jobReference, lat: d.latitude, lng: d.longitude,
          accuracy: d.accuracyMetres, recordedAt: d.recordedAt, stale: false,
        }));
        return next;
      });
    } catch { /* ignore */ }
  }, [carrierId, getToken]);

  useEffect(() => { loadSnapshot(); }, [loadSnapshot]);
  useEffect(() => { const id = setInterval(() => tick(n => n + 1), 60_000); return () => clearInterval(id); }, []);

  const handleLocation = useCallback((update: FleetLocationUpdate) => {
    setLiveDrivers(prev => {
      const next     = new Map(prev);
      const existing = prev.get(update.carrierId);
      next.set(update.carrierId, {
        carrierId: update.carrierId, carrierName: existing?.carrierName ?? 'Carrier',
        jobId: update.jobId, jobReference: update.jobId ? (existing?.jobReference ?? null) : null,
        lat: update.lat, lng: update.lng, accuracy: update.accuracy,
        recordedAt: update.recordedAt, stale: false,
      });
      return next;
    });
    setConnected(true);
  }, []);

  useFleetHub({ getToken, carrierId, onCarrierLocationUpdated: handleLocation, enabled: !!carrierId });

  const employeeList = Array.from(liveDrivers.values()).map(d => ({
    ...d, stale: new Date(d.recordedAt).getTime() < Date.now() - 2 * 60_000,
  }));

  const idleCount  = employeeList.filter(d => !d.jobId).length;
  const onJobCount = employeeList.filter(d =>  d.jobId).length;

  // Only jobs that have coordinates get a route line drawn
  const mappableJobs = myJobs.filter(j => j.pickupLat && j.deliveryLat);

  return (
    <div className="relative h-[calc(100vh-64px)]">
      <h1 className="sr-only">Fleet Map</h1>
      <FleetMap employees={employeeList} jobs={mappableJobs} />

      {/* Status overlay — top-left */}
      <div className="absolute top-3 left-3 z-[1000] bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 px-3 py-2 flex items-center gap-3 text-xs">
        {connected
          ? <Wifi    className="w-3.5 h-3.5 text-green-500 shrink-0" />
          : <WifiOff className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
        <span className={connected ? 'text-green-600 font-medium' : 'text-gray-400'}>
          {connected ? 'Live' : 'Polling'}
        </span>
        {employeeList.length > 0 && (
          <>
            <span className="text-gray-300">·</span>
            <span className="text-gray-600">{employeeList.length} driver{employeeList.length !== 1 ? 's' : ''}</span>
          </>
        )}
      </div>

      {/* Legend — bottom-right */}
      <div className="absolute bottom-6 right-3 z-[1000] bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 px-3 py-2.5 space-y-1.5 text-xs">
        <div className="flex items-center gap-2 text-gray-700">
          <span className="w-3.5 h-3.5 rounded-full bg-blue-600 border border-white shadow-sm shrink-0" />
          Idle ({idleCount})
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <span className="w-3.5 h-3.5 rounded-full bg-red-600 border border-white shadow-sm shrink-0" />
          On job ({onJobCount})
        </div>
        {mappableJobs.length > 0 && (
          <div className="flex items-center gap-2 text-gray-500 border-t border-gray-100 pt-1.5">
            <span className="w-3.5 h-0.5 bg-red-400 shrink-0" style={{ borderTop: '2px dashed #f87171' }} />
            {mappableJobs.length} route{mappableJobs.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Empty state */}
      {employeeList.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-white/90 backdrop-blur rounded-xl px-6 py-5 text-center shadow-lg border border-gray-200">
            <Navigation className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm font-medium text-gray-600">No drivers online</p>
            <p className="text-xs text-gray-400 mt-1">Driver positions appear here once they log in via their portal</p>
            {myJobs.length > 0 && (
              <p className="text-xs text-gray-400 mt-0.5 flex items-center justify-center gap-1">
                <Truck className="w-3 h-3" /> {myJobs.length} active job{myJobs.length !== 1 ? 's' : ''} assigned
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
